import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Droplets, Bell, Sparkles } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ProductHighlights = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.2 });
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["30px", "-30px"]);

  const products = [
    {
      id: 'mava-water',
      name: 'Mava Water',
      status: 'active',
      description: 'Pure, refreshing water sourced from natural springs',
      price: '₹20',
      features: ['100% Pure', 'Natural Minerals', 'pH Balanced', 'BPA Free'],
      gradient: 'from-blue-400 to-blue-600',
      icon: Droplets,
      splashColor: 'rgba(59, 130, 246, 0.3)',
      image: '/api/placeholder/300/400',
      alt: 'Mava Water bottle with crystal clear water and natural spring background'
    },
    {
      id: 'jeera-drink',
      name: 'Jeera Drink',
      status: 'coming-soon',
      description: 'Traditional cumin-flavored refreshing drink',
      price: 'Coming Soon',
      features: ['Authentic Taste', 'Digestive Benefits', 'Natural Spices', 'Refreshing'],
      gradient: 'from-amber-400 to-orange-500',
      icon: Sparkles,
      splashColor: 'rgba(245, 158, 11, 0.3)',
      image: '/api/placeholder/300/400',
      alt: 'Traditional Jeera drink bottle with cumin seeds and spices around it'
    },
    {
      id: 'cola',
      name: 'Cola',
      status: 'coming-soon',
      description: 'Classic cola with a modern twist',
      price: 'Coming Soon',
      features: ['Classic Taste', 'Natural Extracts', 'Fizzy Fresh', 'Premium Quality'],
      gradient: 'from-amber-800 to-red-900',
      icon: Sparkles,
      splashColor: 'rgba(92, 60, 21, 0.3)',
      image: '/api/placeholder/300/400',
      alt: 'Mava Cola bottle with bubbles and classic cola color in modern packaging'
    },
    {
      id: 'mango-juice',
      name: 'Mango Juice',
      status: 'coming-soon',
      description: 'Fresh mango juice from the finest Indian mangoes',
      price: 'Coming Soon',
      features: ['Real Mango', 'No Artificial Colors', 'Rich Flavor', 'Vitamin Rich'],
      gradient: 'from-yellow-400 to-orange-500',
      icon: Sparkles,
      splashColor: 'rgba(251, 191, 36, 0.3)',
      image: '/api/placeholder/300/400',
      alt: 'Mango juice bottle surrounded by fresh ripe mangoes and tropical leaves'
    },
    {
      id: 'goli-soda',
      name: 'Goli Soda',
      status: 'coming-soon',
      description: 'Traditional Indian marble soda with authentic flavors',
      price: 'Coming Soon',
      features: ['Traditional', 'Marble Bottle', 'Authentic Taste', 'Nostalgic'],
      gradient: 'from-green-400 to-teal-500',
      icon: Sparkles,
      splashColor: 'rgba(34, 197, 94, 0.3)',
      image: '/api/placeholder/300/400',
      alt: 'Traditional Goli Soda bottle with marble stopper and colorful Indian street setting'
    }
  ];

  // Notify Me Modal Component
  const NotifyModal = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={() => setShowNotifyModal(false)}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl p-8 max-w-md w-full liquid-shadow"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="text-center">
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-flex items-center justify-center w-16 h-16 bg-mava-gradient rounded-full mb-4"
          >
            <Bell className="w-8 h-8 text-white" />
          </motion.div>
          
          <h3 className="text-2xl font-poppins font-bold text-mava-charcoal mb-2">
            Get Notified!
          </h3>
          <p className="text-gray-600 mb-6">
            Be the first to know when {selectedProduct?.name} launches
          </p>

          <form className="space-y-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mava-orange focus:border-transparent"
            />
            <Button
              type="submit"
              className="w-full bg-mava-orange hover:bg-mava-orange/90 text-white py-3 rounded-lg cursor-glow"
            >
              Notify Me
            </Button>
          </form>

          <button
            onClick={() => setShowNotifyModal(false)}
            className="mt-4 text-gray-500 hover:text-gray-700 brand-transition"
          >
            Maybe later
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <motion.section
      ref={containerRef}
      style={{ y }}
      className="relative py-20 lg:py-32 bg-gradient-to-b from-mava-off-white to-white overflow-hidden"
      id="beverages"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        {/* Floating fruit slices for coming soon products */}
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-20 right-10 w-8 h-8 opacity-20"
        >
          🥭
        </motion.div>
        <motion.div
          animate={{ 
            rotate: -360,
            y: [0, -10, 0]
          }}
          transition={{ 
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-40 left-10 w-6 h-6 opacity-20"
        >
          🍋
        </motion.div>
        <motion.div
          animate={{ 
            rotate: 180,
            x: [0, 10, 0]
          }}
          transition={{ 
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-32 right-20 w-10 h-10 opacity-20"
        >
          🧊
        </motion.div>
      </div>
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="inline-flex items-center justify-center w-16 h-16 bg-mava-gradient rounded-full mb-6 liquid-shadow"
          >
            <Droplets className="w-8 h-8 text-white" />
          </motion.div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-poppins font-bold text-mava-charcoal mb-6">
            Our Beverages
          </h2>
          <p className="text-xl font-inter text-gray-600 max-w-2xl mx-auto">
            From pure water to exciting flavors — discover the Mava range
          </p>
          <div className="w-24 h-1 bg-mava-gradient mx-auto rounded-full mt-6" />
        </motion.div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {products?.map((product, index) => (
            <motion.div
              key={product?.id}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onHoverStart={() => setHoveredProduct(product?.id)}
              onHoverEnd={() => setHoveredProduct(null)}
              className="group relative"
            >
              <motion.div
                whileHover={{ scale: 1.02, y: -5 }}
                className="bg-white rounded-3xl overflow-hidden liquid-shadow brand-transition cursor-pointer h-full"
              >
                {/* Product Status Badge */}
                <div className="absolute top-4 right-4 z-10">
                  {product?.status === 'active' ? (
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-poppins font-medium">
                      Available
                    </span>
                  ) : (
                    <span className="bg-mava-orange text-white px-3 py-1 rounded-full text-sm font-poppins font-medium">
                      Coming Soon
                    </span>
                  )}
                </div>

                {/* Product Image with Splash Effect */}
                <div className="relative h-64 overflow-hidden">
                  <motion.div
                    animate={hoveredProduct === product?.id ? { scale: 1.1 } : { scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className={`absolute inset-0 bg-gradient-to-br ${product?.gradient} opacity-90`}
                  />
                  
                  {/* Splash Animation */}
                  <motion.div
                    animate={hoveredProduct === product?.id ? { 
                      scale: [1, 1.5, 1],
                      opacity: [0.3, 0.6, 0.3]
                    } : {}}
                    transition={{ duration: 0.8, repeat: Infinity }}
                    className="absolute inset-0 rounded-full"
                    style={{ backgroundColor: product?.splashColor }}
                  />

                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      animate={hoveredProduct === product?.id ? { 
                        rotate: [0, 10, -10, 0],
                        scale: [1, 1.1, 1]
                      } : {}}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <product.icon size={80} className="text-white" />
                    </motion.div>
                  </div>

                  {/* Floating bubbles on hover */}
                  {hoveredProduct === product?.id && (
                    <>
                      {[...Array(5)]?.map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ 
                            y: -100,
                            opacity: [0, 1, 0],
                            x: Math.random() * 40 - 20
                          }}
                          transition={{ 
                            duration: 2,
                            delay: i * 0.2,
                            repeat: Infinity
                          }}
                          className="absolute bottom-0 left-1/2 w-2 h-2 bg-white rounded-full"
                        />
                      ))}
                    </>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-2xl font-poppins font-bold text-mava-charcoal">
                      {product?.name}
                    </h3>
                    <span className="text-lg font-poppins font-semibold text-mava-orange">
                      {product?.price}
                    </span>
                  </div>

                  <p className="text-gray-600 font-inter mb-4">
                    {product?.description}
                  </p>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {product?.features?.map((feature, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ delay: 0.3 + i * 0.1 }}
                        className="flex items-center text-sm text-gray-600"
                      >
                        <div className="w-2 h-2 bg-mava-gradient rounded-full mr-2" />
                        {feature}
                      </motion.div>
                    ))}
                  </div>

                  {/* Action Button */}
                  {product?.status === 'active' ? (
                    <Button
                      className="w-full bg-mava-orange hover:bg-mava-orange/90 text-white py-3 rounded-xl cursor-glow water-ripple"
                    >
                      Order Now
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full border-2 border-mava-orange text-mava-orange hover:bg-mava-orange hover:text-white py-3 rounded-xl cursor-glow brand-transition"
                      onClick={() => {
                        setSelectedProduct(product);
                        setShowNotifyModal(true);
                      }}
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Notify Me
                    </Button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
      {/* Notify Modal */}
      {showNotifyModal && <NotifyModal />}
    </motion.section>
  );
};

export default ProductHighlights;